import { Table } from '@components/Table';
import {
  Flex, Pagination, Text
} from '@huspy/briks-web';
import {
  Box, Image, Skeleton
} from '@mantine/core';
import { imagesPath } from '@shared/css.const';
import { FileSignature, FileSpreadsheet } from '@huspy/briks-icons';
import {
  format, formatDistance, isSameDay, isToday
} from 'date-fns';
import { es } from 'date-fns/locale/es';
import { IS_SPAIN_ENV } from '@modules/core/utils';
import { CreditsRoute } from '@modules/core/presentation/v2/routes';
import { useNavigate } from '@tanstack/react-router';
import { CreditsTransactionsResponse } from '@modules/core/api/types';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react';
import { TRANSACTION_ACTION_TYPES } from '@modules/core/api/const';
import transactionsTableStyles from './styles';

const { tableContainer, tableRow, iconContainer } = transactionsTableStyles;

type Props = {
  data?: CreditsTransactionsResponse;
  isLoading?: boolean;
};

const TransactionsTable = ({ data, isLoading }: Props) => {
  const { t } = useTranslation();

  const TRANSACTIONS_MAP = {
    [TRANSACTION_ACTION_TYPES.proposalGenerated]: {
      title: t('core.credits.transactionActions.proposalGenerated'),
      icon: <FileSpreadsheet />,
    },
    [TRANSACTION_ACTION_TYPES.opportunitySubmitted]: {
      title: t('core.credits.transactionActions.opportunitySubmitted'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.firstBankApplicationToHuspySubmitted]: {
      title: t('core.credits.transactionActions.firstBankApplicationToHuspySubmitted'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.secondBankApplicationToHuspySubmitted]: {
      title: t('core.credits.transactionActions.secondBankApplicationToHuspySubmitted'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.otherBankApplicationToHuspySubmitted]: {
      title: t('core.credits.transactionActions.otherBankApplicationToHuspySubmitted'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.firstBankApplicationDirectToBankSubmitted]: {
      title: t('core.credits.transactionActions.firstBankApplicationDirectToBankSubmitted'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.otherBankApplicationDirectToBankSubmitted]: {
      title: t('core.credits.transactionActions.otherBankApplicationDirectToBankSubmitted'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.allocateCredits]: {
      title: t('core.credits.transactionActions.allocateCredits'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.refundCredits]: {
      title: t('core.credits.transactionActions.refundCredits'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.deductCredits]: {
      title: t('core.credits.transactionActions.deductCredits'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.allocateCreditsPurchased]: {
      title: t('core.credits.transactionActions.allocateCreditsPurchased'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.refundCreditsPurchased]: {
      title: t('core.credits.transactionActions.refundCreditsPurchased'),
      icon: <FileSignature />,
    },
    [TRANSACTION_ACTION_TYPES.deductCreditsExpired]: {
      title: t('core.credits.transactionActions.deductCreditsExpired'),
      icon: <FileSignature />,
    },
  };

  const navigate = useNavigate({ from: CreditsRoute.fullPath });

  const tableRows = data?.transactions;

  const handlePageSelect = (value: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: value,
      }),
    });
  };

  const locale = IS_SPAIN_ENV ? es : undefined;

  let currentDay = new Date(0);

  return (
    <Flex direction='column' gap={ 4 } flex={ 1 }>
      <Box className={ tableContainer }>
        <Table.Root>
          <Table.Body>
            {isLoading ? (
              <Table.Row key='table-loading-row'>
                {new Array(5).fill(0).map((__) => (
                  <Table.Cell key='table-loading-cell'>
                    <Skeleton height={ 30 } w='100%' radius='4px' />
                  </Table.Cell>
                ))}
              </Table.Row>
            ) : (
              <>
                {tableRows?.map((row, i) => {
                  const d = new Date(row.timestamp);

                  const transaction = TRANSACTIONS_MAP[row.action_type] ?? {
                    title: row.action_type,
                    icon: <FileSpreadsheet />,
                  };

                  const isTheSameDay = isSameDay(currentDay, d);

                  if (!isTheSameDay) {
                    currentDay = d;
                  }

                  return (
                    <Fragment key={ row.transaction_id }>

                      {!isTheSameDay ? (
                        <Table.Row className={ tableRow }>
                          <Table.Cell colSpan={ 2 }>
                            <Flex paddingTop={ i > 0 ? 8 : 0 }>
                              <Text size='md' color='text-secondary' weight='semiBold'>
                                { isToday(d)
                                  ? t('core.credits.today')
                                  : format(d, 'MMM dd, yyyy', { locale }) }
                              </Text>
                            </Flex>
                          </Table.Cell>
                        </Table.Row>
                      ) : null}

                      <Table.Row className={ tableRow }>
                        <Table.Cell style={ { width: '1%' } }>
                          <Flex className={ iconContainer } justify='center' align='center'>
                            {transaction.icon}
                          </Flex>
                        </Table.Cell>

                        <Table.Cell>
                          <Flex direction='column'>
                            <Text size='lg'>{transaction.title}</Text>
                            {/* <Text size='md' color='text-tertiary'>{row.action_type}</Text> */}
                          </Flex>
                        </Table.Cell>

                        <Table.Cell>
                          <Text size='md' color='text-tertiary'>
                            {
                              isToday(d)
                                ? formatDistance(
                                  d,
                                  new Date(),
                                  {
                                    addSuffix: true,
                                    locale,
                                  }
                                )
                                : format(d, 'hh:mm a')
                            }
                          </Text>
                        </Table.Cell>

                        <Table.Cell>
                          <Flex gap={ 1 } align='center' justify='flex-end'>
                            {Number(row.amount) < 0 ? (
                              <Text size='2xl' color='text-error-primary_alt'>-</Text>
                            ) : (
                              <Text size='2xl' color='text-success-primary_alt'>+</Text>
                            )}

                            <Text size='2xl' weight='semiBold'>{Math.abs(Number(row.amount))}</Text>
                            <Image
                              src={ `${imagesPath}/credits.png` }
                              w={ 32 }
                            />
                          </Flex>
                        </Table.Cell>
                      </Table.Row>

                    </Fragment>
                  );
                })}
              </>
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      <Flex justify='flex-end'>
        <Pagination
          count={ data?.total ?? 0 }
          pageSize={ data?.limit }
          siblingCount={ 1 }
          defaultPage={ data?.page }
          page={ data?.page }
          onPageChange={ (change: { page: number, pageSize: number }) => { handlePageSelect(change.page); } }
        />
      </Flex>
    </Flex>
  );
};

export default TransactionsTable;
